import React, { useState, useEffect } from "react";
import TaskTitles from "./components/atomic/TaskTitles";
import TaskInput from "./components/atomic/TaskInput";
import Slider from "./components/Slider";
import SaveButton from "./components/atomic/SaveButton";
import AI from "./assets/ai.svg";
import axios from 'axios';
import { BASE_SETTINGS_URL } from '../../components/constants';
import { useToast } from '../../components/Toast';
import { useSelector } from 'react-redux';

const API_URL = `${BASE_SETTINGS_URL}/chat`;

const DEFAULT_CHAT = {
  name: "Chatbot",
  system_prompt: "",
  temperature: 0.5,
  topp: 0.9,
  num_contexts: 5, // default value
};

const ChatSettings = () => {
  const [formData, setFormData] = useState(DEFAULT_CHAT);
  const [chatId, setChatId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { addToast, ToastContainer } = useToast();
  const { data } = useSelector((state) => state.admin);
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN";

  // Fetch or create the single chat row on mount
  useEffect(() => {
    const fetchOrCreateChat = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(API_URL);
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
          setFormData({
            name: data[0].name || "Chatbot",
            system_prompt: data[0].system_prompt || "",
            temperature: data[0].temperature ?? 0.5,
            topp: data[0].topp ?? 0.9,
            num_contexts: data[0].num_contexts ?? 5,
          });
          setChatId(data[0].id);
        } else {
          // No row exists, create one
          try {
            const createRes = await axios.post(API_URL, DEFAULT_CHAT);
            const chat = createRes.data;
            setFormData({
              name: chat.name || "Chatbot",
              system_prompt: chat.system_prompt || "",
              temperature: chat.temperature ?? 0.5,
              topp: chat.topp ?? 0.9,
              num_contexts: chat.num_contexts ?? 5,
            });
            setChatId(chat.id);
            addToast("Chat-Konfiguration wurde erstellt.", "success");
          } catch (createErr) {
            addToast("Fehler beim Erstellen der Chat-Konfiguration.", "error");
          }
        }
      } catch (err) {
        addToast("Netzwerkfehler beim Laden der Chat-Einstellungen.", "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrCreateChat();
    // eslint-disable-next-line
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSliderChange = (e, field) => {
    const value = Number(e.target.value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!chatId) {
        addToast("Keine Chat-Konfiguration zum Aktualisieren gefunden.", "error");
        setIsSaving(false);
        return;
      }
      const cleanFormData = {
        ...formData,
        temperature: formData.temperature === "" ? null : formData.temperature,
        topp: formData.topp === "" ? null : formData.topp,
        num_contexts: formData.num_contexts === "" ? null : formData.num_contexts,
      };
      const res = await axios.put(`${API_URL}/${chatId}`, cleanFormData);
      setChatId(res.data.id);
      addToast("Chat KI-Profil erfolgreich gespeichert", "success");
    } catch (err) {
      addToast("Netzwerkfehler beim Speichern der Chat-Einstellungen.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mb-16 pt-5 mt-8 mx-auto px-4 sm:px-6 rounded lg:px-8">
      <ToastContainer />
      <div className="pl-32 pr-40">
        <h1 className="text-2xl font-semibold mb-8">Chat KI-Profil bearbeiten</h1>
        {isLoading ? (
          <div className="py-10 text-center text-gray-500">Lade Einstellungen...</div>
        ) : (
          <>
            <div className="flex justify-between border-b border-gray-200 pb-10">
              <TaskTitles
                title="Chat KI-Profil & Name"
                description="Geben Sie Ihrem Chatbot einen Namen."
              />
              <div className="w-[60%]">
                <TaskInput
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Geben Sie dem Chatbot einen Namen."
                  title="Chatbot Name"
                  icon={AI}
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>

            <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
              <TaskTitles
                title="System Prompt"
                description="Geben Sie den System Prompt für den Chatbot ein."
              />
              <div className="w-[60%] flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h1 className="font-bold">System Prompt</h1>
                  <textarea
                    name="system_prompt"
                    value={formData.system_prompt}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-lg resize-none h-44"
                    placeholder="Geben Sie den System Prompt ein."
                    disabled={!isSuperAdmin}
                  />
                </div>
                <Slider
                  label="Temperature"
                  value={formData.temperature}
                  onChange={(e) => handleSliderChange(e, "temperature")}
                  disabled={!isSuperAdmin}
                />
                <Slider
                  label="Top P"
                  value={formData.topp}
                  onChange={(e) => handleSliderChange(e, "topp")}
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>
            <div className="flex justify-between border-b border-gray-200 pb-10 pt-10">
              <TaskTitles
                title="Anzahl Kontexte"
                description="Geben Sie die Anzahl der Kontexte an, die der Chatbot verarbeiten soll."
              />
              <div className="w-[60%] flex flex-col gap-4">
                <TaskInput
                  name="num_contexts"
                  value={formData.num_contexts}
                  onChange={handleInputChange}
                  placeholder="Number of contexts"
                  title="Anzahl Kontexte"
                  icon={AI}
                  disabled={!isSuperAdmin}
                  type="number"
                />
              </div>
            </div>
            <div className="flex justify-end pt-5 pb-10">
              <SaveButton
                onClick={handleSave}
                isLoading={isSaving}
                disabled={isSaving}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatSettings; 